<template>
  <!--
    Attach one Stripe coupon to a list of customers given by email. Preview
    first: the server says what it will do for each email (customers who
    already hold a coupon are skipped, never replaced), then the apply button
    writes the ready ones that are still checked, with a required note that
    goes on each of those customers as a CS note.
  -->
  <v-dialog :value="value" max-width="900px" persistent scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-account-multiple-plus-outline</v-icon>
        Add Coupon to Customers
        <v-spacer/>
        <v-btn icon :disabled="busy" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="code"
              label="Coupon code"
              placeholder="FED10"
              outlined
              dense
              :disabled="busy || applied"
              @input="clearPreview"
              @keydown.enter="preview"
            />
          </v-col>
          <v-col cols="12" md="8">
            <v-textarea
              v-model="emailText"
              label="Customer emails"
              placeholder="one per line, or separated by commas"
              outlined
              dense
              rows="4"
              auto-grow
              :disabled="busy || applied"
              :hint="emailCount ? `${emailCount} ${emailCount === 1 ? 'email' : 'emails'}` : ''"
              persistent-hint
              @input="clearPreview"
            />
          </v-col>
        </v-row>

        <v-alert v-if="error" dense outlined type="error" class="mt-2">{{ error }}</v-alert>

        <template v-if="result">
          <div class="mb-2">
            <code>{{ result.coupon.id }}</code>
            <span v-if="result.coupon.name"> - {{ result.coupon.name }}</span>
            <v-chip x-small class="ml-2">{{ result.kind }}</v-chip>
            <span class="ml-2">{{ valueText(result.coupon) }}, {{ durationText(result.coupon) }}</span>
          </div>
          <v-alert v-for="(note, i) in result.notes" :key="`n${i}`" dense outlined type="info" class="mb-2">
            {{ note }}
          </v-alert>

          <div class="d-flex flex-wrap align-center mb-2">
            <v-chip v-for="status in statusesShown" :key="status" small outlined class="mr-2 mb-1" :color="statusColor(status)">
              {{ counts[status] }} {{ status }}
            </v-chip>
            <v-spacer/>
            <template v-if="!applied && readyRows.length">
              <v-btn text x-small :disabled="busy" @click="selectAll(true)">Select all</v-btn>
              <v-btn text x-small :disabled="busy" @click="selectAll(false)">Select none</v-btn>
            </template>
          </div>

          <v-simple-table dense>
            <thead>
            <tr>
              <th v-if="!applied" width="40"></th>
              <th>Email</th>
              <th>Customer</th>
              <th>Status</th>
              <th>Existing coupon</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="row in result.rows" :key="row.email">
              <td v-if="!applied">
                <v-simple-checkbox
                  v-if="row.status === 'ready'"
                  :value="selected[row.email] !== false"
                  :disabled="busy"
                  color="primary"
                  :ripple="false"
                  @input="$set(selected, row.email, $event)"
                />
              </td>
              <td>{{ row.email }}</td>
              <td>
                <router-link v-if="row.uid" :to="{name: 'Customer', params: {id: row.uid}}" target="_blank">{{ row.uid }}</router-link>
              </td>
              <td>
                <v-chip x-small outlined :color="statusColor(row.status)">{{ row.status }}</v-chip>
                <span v-if="row.error" class="red--text caption ml-1">{{ row.error }}</span>
              </td>
              <td>
                <template v-if="row.existing_coupon">
                  <code>{{ row.existing_coupon.id }}</code>
                  <span v-if="row.existing_coupon.name" class="caption"> - {{ row.existing_coupon.name }}</span>
                </template>
              </td>
            </tr>
            </tbody>
          </v-simple-table>
        </template>
        <div v-else class="caption text--secondary mt-2">
          Preview to see what will happen to each customer before anything is written. Untick anyone who should not get it.
        </div>
      </v-card-text>
      <div v-if="result && !applied" class="px-6 pt-2">
        <v-textarea
          v-model="reason"
          label="Note (why, required)"
          rows="2"
          auto-grow
          :hint="`Added as a CS note, with the coupon's details, to each of the ${readyCount} ${readyCount === 1 ? 'customer' : 'customers'} who get this coupon`"
          persistent-hint
          :disabled="busy"
        />
      </div>
      <v-card-actions>
        <v-spacer/>
        <v-btn text :disabled="busy" @click="close">{{ applied ? 'Close' : 'Cancel' }}</v-btn>
        <v-btn v-if="!applied" outlined :loading="previewing" :disabled="!canPreview || applying" @click="preview">Preview</v-btn>
        <v-btn v-if="!applied" color="primary" :loading="applying" :disabled="!readyCount || previewing || !reason.trim()" @click="apply">
          Apply to {{ readyCount }} {{ readyCount === 1 ? 'customer' : 'customers' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api';
import {formatCurrency} from '@/store/utils';

const STATUS_COLORS = {
  ready: 'primary',
  applied: 'green',
  failed: 'red',
  'has coupon': 'orange',
  'has this coupon': 'grey',
  'no user': 'red',
  'no stripe customer': 'red',
  'not selected': 'grey',
};
const STATUS_ORDER = ['ready', 'applied', 'failed', 'not selected', 'has coupon', 'has this coupon', 'no user', 'no stripe customer'];

export default {
  name: 'CouponBulkApplyDialog',
  props: {
    value: {type: Boolean, default: false},
    // the code the dialog opens with
    coupon: {type: String, default: ''},
  },
  data() {
    return {
      code: '',
      emailText: '',
      reason: '',
      result: null,
      // email -> false for a ready row that was unticked
      selected: {},
      applied: false,
      previewing: false,
      applying: false,
      error: null,
    };
  },
  computed: {
    busy() {
      return this.previewing || this.applying;
    },
    emailCount() {
      return this.emailText.split(/[,;\s]+/).filter(Boolean).length;
    },
    canPreview() {
      return !!(this.code || '').trim() && this.emailCount > 0;
    },
    readyRows() {
      return this.result ? this.result.rows.filter(r => r.status === 'ready') : [];
    },
    checkedRows() {
      return this.readyRows.filter(r => this.selected[r.email] !== false);
    },
    readyCount() {
      return this.applied ? 0 : this.checkedRows.length;
    },
    counts() {
      const counts = {};
      for (const row of this.result.rows) counts[row.status] = (counts[row.status] || 0) + 1;
      return counts;
    },
    statusesShown() {
      return STATUS_ORDER.filter(s => this.counts[s]);
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.code = this.coupon || '';
        this.emailText = '';
        this.reason = '';
        this.clearPreview();
        this.applied = false;
      }
    },
  },
  methods: {
    close() {
      this.$emit('input', false);
    },

    clearPreview() {
      this.result = null;
      this.selected = {};
      this.error = null;
    },

    selectAll(on) {
      const selected = {};
      for (const row of this.readyRows) selected[row.email] = on;
      this.selected = selected;
    },

    async preview() {
      if (!this.canPreview) return;
      this.previewing = true;
      this.error = null;
      try {
        const {data} = await api.post('v2/stripe/coupons/bulk-apply', {coupon: this.code.trim(), emails: this.emailText});
        this.result = data;
        this.selected = {};
      } catch (e) {
        this.result = null;
        this.error = this.messageOf(e, 'Could not preview');
      } finally {
        this.previewing = false;
      }
    },

    async apply() {
      this.applying = true;
      this.error = null;
      try {
        const emails = this.checkedRows.map(r => r.email);
        const {data} = await api.post('v2/stripe/coupons/bulk-apply', {coupon: this.code.trim(), emails, apply: true, reason: this.reason.trim()});
        // the answer covers the checked rows only; the rest keep their preview, unticked ready ones marked as left out
        const outcomes = Object.fromEntries(data.rows.map(r => [r.email, r]));
        const rows = this.result.rows.map(r => outcomes[r.email] || (r.status === 'ready' ? {...r, status: 'not selected'} : r));
        this.result = {...data, rows};
        this.applied = true;
        const {applied, failed} = data.counts;
        this.$store.commit('showSnackbar', {
          message: `Coupon ${data.coupon.id} applied to ${applied} ${applied === 1 ? 'customer' : 'customers'}${failed ? `, ${failed} failed` : ''}`,
          color: failed ? 'warning' : 'success',
          timeout: 6000,
        });
        this.$emit('applied', data);
      } catch (e) {
        this.error = this.messageOf(e, 'Failed to apply the coupon');
      } finally {
        this.applying = false;
      }
    },

    statusColor(status) {
      return STATUS_COLORS[status] || 'grey';
    },

    valueText(c) {
      if (c.percent_off != null) return `${c.percent_off}% off`;
      if (c.amount_off != null) return `${formatCurrency(c.amount_off)} off`;
      return '';
    },

    durationText(c) {
      if (c.duration === 'repeating') return `${c.duration_in_months} months`;
      return c.duration;
    },

    messageOf(e, fallback) {
      return (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || fallback;
    },
  },
};
</script>
